import { Component, Input, OnChanges } from '@angular/core';
import type { EChartsOption } from 'echarts';

// Status-chip contract shared with the tiles that feed it (e.g. timesheet.status).
export interface StatusChip {
  status: 'good' | 'warning' | 'critical';
  labelKey: string;
}

@Component({
  selector: 'bst-kpi-card',
  templateUrl: './kpi-card.component.html',
  styleUrls: ['./kpi-card.component.scss'],
  standalone: false
})
export class KpiCardComponent implements OnChanges {
  @Input() value = '';
  @Input() suffix = '';
  @Input() deltaText = '';
  @Input() sparkline: number[] | null = null;
  @Input() bullet: { value: number | null; target: number | null } | null = null;
  @Input() size: 'normal' | 'hero' = 'normal';
  @Input() statusChip: StatusChip | null = null;

  sparkOptions: EChartsOption | null = null;

  ngOnChanges(): void {
    // Spec §7: fewer than 2 non-zero points reads as a flat line with one stray
    // tick, not a trend. The kv value above is already the labelled number this
    // widget leads with, so the degenerate case just drops the sparkline.
    const hasSignal = (this.sparkline?.filter(v => v !== 0).length ?? 0) >= 2;
    this.sparkOptions = this.sparkline && hasSignal ? {
      grid: { left: 0, right: 0, top: 4, bottom: 0 },
      xAxis: { type: 'category', show: false },
      yAxis: { type: 'value', show: false, scale: true },
      series: [{ type: 'line', data: this.sparkline, symbol: 'none', lineStyle: { color: '#1863DC', width: 2 } }],
      animation: false,
    } : null;
  }

  // RAG status is a semantic verdict independent of the bar's rendered width: neutral
  // when there is no value or no positive target to compare against, otherwise
  // green/amber/red against the higher-is-better target ratio.
  get ragStatus(): 'green' | 'amber' | 'red' | 'neutral' {
    const v = this.bullet?.value, t = this.bullet?.target;
    if (v == null || t == null || t <= 0) return 'neutral';
    if (v >= t) return 'green';
    if (v >= t * 0.9) return 'amber';
    return 'red';
  }

  // The bullet fill itself is always the neutral accent blue now (color is not sentiment);
  // the graded status instead surfaces as this chip, reusing the finance.rag.* copy that
  // used to be aria-only text. Null when there is no target to judge against (ragStatus
  // 'neutral'), matching the bare informational bar in that case.
  get bulletChip(): StatusChip | null {
    const status = this.ragStatus;
    if (status === 'neutral') return null;
    return {
      status: status === 'green' ? 'good' : status === 'amber' ? 'warning' : 'critical',
      labelKey: 'finance.rag.' + status,
    };
  }

  get bulletWidthPct(): number {
    return Math.max(0, Math.min(100, this.bullet?.value ?? 0));
  }

  get bulletTargetPct(): number {
    return Math.max(0, Math.min(100, this.bullet?.target ?? 0));
  }

  ragLabelKey(): string {
    return 'finance.rag.' + this.ragStatus;
  }

  chipIcon(status: StatusChip['status']): string {
    return status === 'good' ? 'checkmark-circle-2-outline'
      : status === 'warning' ? 'clock-outline'
      : 'close-circle-outline';
  }
}
